import { FC } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CounterClockwiseClockIcon, TrashIcon } from "@radix-ui/react-icons";
import { Button } from "../ui/button";
import { useDocumentHistoryView } from "./DocumentHistoryView.hooks";
import { ConfirmMessageDialog } from "../MessageDialog";
import { Link } from "react-router-dom";
import { DocumentHistoryEntry } from "@/atoms/documentHistory";

const documentHistoryTitle = (entry?: DocumentHistoryEntry) => {
  return `P.${entry?.pageNo ?? "0"}:${entry?.documentTitle ?? ""}`;
};

const DocumentHistorySection: FC<{
  label: string;
  entries: DocumentHistoryEntry[];
  onClickTrash: (entry: DocumentHistoryEntry) => void;
}> = ({ label, entries, onClickTrash }) => {
  if (entries.length === 0) return null;
  return (
    <>
      <div className="mx-2 rounded-sm px-2 pb-1 pt-2">
        <p className="text-base font-bold leading-7 text-primary">{label}</p>
      </div>
      {entries.map((entry, index) => {
        const params = new URLSearchParams();
        params.append("page", entry.pageNo.toString());
        const linkTo = `/documents/${entry.documentId}?${params.toString()}`;

        return (
          <DropdownMenuItem
            key={`${entry.documentId}-${entry.pageNo}-${index}`}
            className="mx-2 flex h-8 max-h-8 flex-row gap-x-2 pl-2 pr-0 data-[disabled]:opacity-100"
          >
            <Link className="flex-1" to={linkTo} target="_blank" onClick={(e) => e.stopPropagation()}>
              <p className="max-h-8 max-w-[228px] flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-base text-primary">
                {documentHistoryTitle(entry)}
              </p>
            </Link>

            <div className="min-h-8 min-w-8">
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClickTrash(entry);
                }}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </div>
          </DropdownMenuItem>
        );
      })}
    </>
  );
};

export const DocumentHistoryView: FC = () => {
  const {
    t,
    documentHistory,
    grouped,
    open,
    setOpen,
    onClickTrash,
    deleteEntry,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    callDeleteEntry,
    onClickDeleteAll,
    deleteAllConfirmOpen,
    setDeleteAllConfirmOpen,
    callDeleteAll,
  } = useDocumentHistoryView();

  return (
    <>
      <div className="flex flex-col">
        <Button className="h-10 w-10 p-0" variant="ghost" onClick={() => setOpen(!open)}>
          <CounterClockwiseClockIcon className="h-4 w-4" />
        </Button>

        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger className="w-10" />

          <DropdownMenuContent className="w-[296px] max-w-[100vw] p-0 sm:w-[296px] sm:max-w-[296px]" align="end">
            <div className="max-h-[calc(100svh-150px)] overflow-y-auto overflow-x-hidden py-2 sm:max-h-[calc(100svh-150px)]">
              {documentHistory.length === 0 && (
                <div className="px-4 py-1">
                  <p className="text-base text-primary">{t("document_history_empty")}</p>
                </div>
              )}
              <DocumentHistorySection label={t("document_history_today")} entries={grouped.today} onClickTrash={onClickTrash} />
              <DocumentHistorySection label={t("document_history_this_week")} entries={grouped.thisWeek} onClickTrash={onClickTrash} />
              <DocumentHistorySection label={t("document_history_before")} entries={grouped.before} onClickTrash={onClickTrash} />
              {documentHistory.length > 0 && (
                <>
                  <DropdownMenuSeparator className="mx-2 my-2 bg-border" />
                  <DropdownMenuItem
                    className="mx-2 flex h-8 max-h-8 cursor-pointer flex-row pl-2 pr-0 data-[disabled]:opacity-100"
                    onClick={(e) => {
                      e.preventDefault();
                      onClickDeleteAll();
                    }}
                  >
                    <p className="max-h-8 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-base">
                      {t("document_history_delete_all")}
                    </p>
                  </DropdownMenuItem>
                </>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ConfirmMessageDialog
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        title={t("document_history_delete_title", { title: documentHistoryTitle(deleteEntry) })}
        message={t("document_history_delete_message")}
        actionButtonLabel={t("delete")}
        actionButtonBg="bg-destructive"
        onClickAction={callDeleteEntry}
        cancelButtonLabel={t("cancel")}
      />

      <ConfirmMessageDialog
        open={deleteAllConfirmOpen}
        setOpen={setDeleteAllConfirmOpen}
        title={t("document_history_delete_all_title")}
        message={t("document_history_delete_all_message")}
        actionButtonLabel={t("delete")}
        actionButtonBg="bg-destructive"
        onClickAction={callDeleteAll}
        cancelButtonLabel={t("cancel")}
      />
    </>
  );
};
